import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Select from 'react-select';
import FormControl from '@mui/material/FormControl';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid2';
import { Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TextField from '@mui/material/TextField';
import { useDispatch } from 'react-redux';
import { editUiSpec } from 'actions/userActions';

const formControlStyles = { display: 'flex', width: '100%', marginBottom: '14px' };
const formFieldStyles = {
    control: (provided) => ({ ...provided, padding: '6px', borderRadius: '12px' }),
};

const toSelectOptions = (items = [], key) => items.map((item) => ({ value: item[key], label: item[key] }));
const toOption = (val) => (val ? { value: val, label: val } : null);

const makeFormatOptionLabel = (currentValue) => (option, { context }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>{option.label}</span>
        {context === 'menu' && currentValue?.value === option.value && (
            <CheckCircleIcon style={{ color: '#4caf50', fontSize: '16px', marginLeft: '8px' }} />
        )}
    </div>
);

const buildFormData = (row) => ({
    productLine: toOption(row?.productLine),
    tabCategory: toOption(row?.tabCategory),
    verticalStepperHeader: toOption(row?.verticalStepperHeader),
    verticalStepperHeaderText: row?.verticalStepperHeader || '',
    subSectionOrderNextTo: null,
    choiceId: toOption(row?.choiceId),
    choiceName: toOption(row?.choiceName),
    choiceOrderNextTo: null,
});

const FieldRow = ({ label, children }) => (
    <Grid container spacing={2} alignItems="center" sx={formControlStyles}>
        <Grid sx={{ width: '200px' }}>
            <Typography variant="body1">{label}</Typography>
        </Grid>
        <Grid sx={{ flex: 1 }}>
            <FormControl fullWidth>{children}</FormControl>
        </Grid>
    </Grid>
);

const SelectField = ({ label, value, onChange, options, placeholder, isDisabled }) => {
    const formatOptionLabel = useMemo(() => makeFormatOptionLabel(value), [value]);
    return (
        <FieldRow label={label}>
            <Select
                styles={formFieldStyles}
                placeholder={placeholder}
                value={value}
                options={options}
                onChange={onChange}
                menuPosition="fixed"
                isClearable={false}
                isDisabled={isDisabled}
                formatOptionLabel={formatOptionLabel}
            />
        </FieldRow>
    );
};

const TextInputField = ({ label, value, onChange, placeholder, isDisabled }) => (
    <FieldRow label={label}>
        <TextField
            fullWidth
            size="small"
            placeholder={placeholder}
            value={value || ''}
            onChange={onChange}
            disabled={isDisabled}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', padding: '6px' } }}
        />
    </FieldRow>
);

export const EditDialogForUiSpec = ({ open, handleClose, ocUiSpecNested, selectedRow }) => {
    const dispatch = useDispatch();
    const [formData, setFormData] = useState(() => buildFormData(null));

    useEffect(() => {
        if (open && selectedRow) setFormData(buildFormData(selectedRow));
    }, [open, selectedRow]);

    const { productLine, tabCategory, verticalStepperHeader, verticalStepperHeaderText, subSectionOrderNextTo, choiceId, choiceName, choiceOrderNextTo } = formData;

    const selectedProductLine = useMemo(
        () => ocUiSpecNested.find((cat) => cat.productLine === productLine?.value) ?? null,
        [productLine, ocUiSpecNested]
    );
    const selectedTabCategory = useMemo(
        () => selectedProductLine?.tabCategories?.find((tab) => tab.tabCategory === tabCategory?.value) ?? null,
        [tabCategory, selectedProductLine]
    );
    const selectedSubSection = useMemo(
        () => selectedTabCategory?.subSections?.find((sub) => sub.verticalStepperHeader === verticalStepperHeader?.value) ?? null,
        [verticalStepperHeader, selectedTabCategory]
    );

    const productLineList = useMemo(() => toSelectOptions(ocUiSpecNested, 'productLine'), [ocUiSpecNested]);
    const tabCategoryList = useMemo(() => toSelectOptions(selectedProductLine?.tabCategories, 'tabCategory'), [selectedProductLine]);
    const subSectionNameList = useMemo(() => toSelectOptions(selectedTabCategory?.subSections, 'verticalStepperHeader'), [selectedTabCategory]);
    const choiceNameList = useMemo(() => toSelectOptions(selectedSubSection?.choices, 'choiceName'), [selectedSubSection]);
    const choiceIdList = useMemo(() => toSelectOptions(selectedSubSection?.choices, 'choiceId'), [selectedSubSection]);

    const handleChange = useCallback((field) => (eventOrOption) => {
        const value = eventOrOption?.target ? eventOrOption.target.value : eventOrOption;
        setFormData((prev) => {
            const next = { ...prev, [field]: value };
            if (field === 'verticalStepperHeader') next.verticalStepperHeaderText = value?.value ?? '';
            return next;
        });
    }, []);

    const handleReset = useCallback(() => setFormData(buildFormData(selectedRow)), [selectedRow]);
    const handleSubmit = useCallback(() => {
        const payload = {
            productLine: productLine?.value,
            tabCategory: tabCategory?.value,
            verticalStepperHeader: verticalStepperHeader?.value,
            newVerticalStepperHeader: verticalStepperHeaderText,
            subSectionOrderNextTo: subSectionOrderNextTo?.value ?? null,
            choiceId: choiceId?.value,
            choiceName: choiceName?.value,
            choiceOrderNextTo: choiceOrderNextTo?.value ?? null,
        };
        dispatch(editUiSpec(payload));
        handleClose();
    }, [productLine, tabCategory, verticalStepperHeaderText, subSectionOrderNextTo, choiceId, choiceName, choiceOrderNextTo, handleClose]);


    const commonFields = useMemo(() => [
        { label: 'Product Line', fieldName: 'productLine', value: productLine, isDisabled: true, options: productLineList, placeholder: 'Select Product Line' },
        { label: 'Tab Category', fieldName: 'tabCategory', value: tabCategory, isDisabled: true, options: tabCategoryList, placeholder: 'Select Tab Category' },
        { label: 'Vertical Stepper Header', fieldName: 'verticalStepperHeader', value: verticalStepperHeader, isDisabled: false, options: subSectionNameList, placeholder: 'Select Vertical Stepper Header' },
        { label: 'New Vertical Stepper Header', fieldName: 'verticalStepperHeaderText', value: verticalStepperHeaderText, isDisabled: false, type: 'text', placeholder: 'Edit Vertical Stepper Header' },
        { label: 'Sub Section Order Next to', fieldName: 'subSectionOrderNextTo', value: subSectionOrderNextTo, isDisabled: !tabCategory, options: subSectionNameList, placeholder: 'Insert After Vertical Stepper Header' },
        { label: 'Choice ID', fieldName: 'choiceId', value: choiceId, isDisabled: true, options: choiceIdList, placeholder: 'Enter Choice ID' },
        { label: 'Choice Name', fieldName: 'choiceName', value: choiceName, isDisabled: true, options: choiceNameList, placeholder: 'Enter Choice Name' },
        { label: 'Choice Order Next to', fieldName: 'choiceOrderNextTo', value: choiceOrderNextTo, isDisabled: !verticalStepperHeader, options: choiceNameList, placeholder: 'Insert After Choice Name' },
    ], [productLine, tabCategory, verticalStepperHeader, verticalStepperHeaderText, subSectionOrderNextTo, choiceId, choiceName, choiceOrderNextTo, productLineList, tabCategoryList, subSectionNameList, choiceIdList, choiceNameList]);

    return (
        <Dialog open={open} onClose={handleClose} className="custom-dialog" PaperProps={{ sx: { minWidth: '600px' } }}>
            <DialogTitle variant="h6">EDIT Choice/Sub-Section</DialogTitle>
            <DialogContent>
                {commonFields.map(({ fieldName, type, ...props }) =>
                    type === 'text'
                        ? <TextInputField key={fieldName} {...props} onChange={handleChange(fieldName)} />
                        : <SelectField key={fieldName} {...props} onChange={handleChange(fieldName)} />
                )}
            </DialogContent>
            <DialogActions className="dialog-actions">
                <Button variant="outlined" className="cancel-button" onClick={handleClose}>Cancel</Button>
                <Button variant="outlined" className="cancel-button" onClick={handleReset}>Reset</Button>
                <Button variant="contained" className="add-Button" onClick={handleSubmit} >Update</Button>
            </DialogActions>
        </Dialog>
    );
};